import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Card, CardContent } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Skeleton } from '../ui/skeleton'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select'
import { SURVEY_TONES } from '../conversations/thread'
import { LEVELS, useGroupMatrix, useGroupSites, type GroupKPIMeta, type GroupPerimeterRef, type GroupSiteCell, type GroupSiteRow, type Level, type Period } from '../../api/groupSteering'
import { TIER_TEXT, formatUnit, langOf, npsCounts, npsUncertainty, tierOf, type NpsCounts } from './format'
import { DefinitionPopover, NpsCompositionBar } from './primitives'

export type SitesSort = 'answers' | 'nps' | 'detractors'
export const SITES_SORTS: SitesSort[] = ['answers', 'nps', 'detractors']

/** The sentinel of the perimeter select for "the whole scope": Radix refuses an empty value. */
const WHOLE_SCOPE = 'all'

/**
 * Order the site rows on the client. Answers and detractors put the largest
 * first; the NPS puts the lowest first (the sites to look at), with the
 * withheld scores last since they carry no value to compare.
 */
export function sortSites(rows: GroupSiteRow[], sort: SitesSort): GroupSiteRow[] {
    const byName = (a: GroupSiteRow, b: GroupSiteRow) => a.name.localeCompare(b.name)
    const out = [...rows]
    switch (sort) {
        case 'nps':
            return out.sort((a, b) => {
                const av = a.suppressed ? null : a.value
                const bv = b.suppressed ? null : b.value
                if (av === null && bv === null) return b.n - a.n || byName(a, b)
                if (av === null) return 1
                if (bv === null) return -1
                return av - bv || b.n - a.n || byName(a, b)
            })
        case 'detractors':
            return out.sort((a, b) => npsCounts(b.breakdown).detractors - npsCounts(a.breakdown).detractors || b.n - a.n || byName(a, b))
        default:
            return out.sort((a, b) => b.n - a.n || byName(a, b))
    }
}

/**
 * NPS by site (spec 2026-10-02-02): the pooled reading of the scope with its
 * composition, then one row per site of the scope — its answers out of its
 * closed requests, the composition bar, and the score, withheld under the
 * registry floor. The counts always show: they are not a signal, so the floor
 * does not hide them.
 */
export function SitesView({
    org,
    period,
    perimeter,
    sort,
    onPerimeterChange,
    onSortChange,
}: {
    org: string
    period: Period
    perimeter: string | undefined
    sort: SitesSort
    onPerimeterChange: (uid: string | undefined) => void
    onSortChange: (sort: SitesSort) => void
}) {
    const { t } = useTranslation('group-steering')
    const sites = useGroupSites(org, period, perimeter)
    const rows = useMemo(() => sortSites(sites.data?.sites ?? [], sort), [sites.data, sort])
    const kpi = sites.data?.kpi

    return (
        <div className="space-y-4" data-testid="group-sites-page">
            <PageHeader icon={SECTION_ICONS.group_steering} title={t('sites.title')} description={t('sites.intro')} data-testid="group-sites-title" />
            <div className="flex flex-wrap items-center gap-3" data-testid="group-sites-filters">
                <PerimeterSelect org={org} period={period} value={perimeter} scope={sites.data?.scope} onChange={onPerimeterChange} />
                <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label={t('sites.sort_label')} data-testid="group-sites-sort">
                    <span className="text-xs text-muted-foreground mr-1">{t('sites.sort_label')}</span>
                    {SITES_SORTS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            role="radio"
                            aria-checked={sort === s}
                            onClick={() => onSortChange(s)}
                            className={`px-2 py-0.5 rounded-full border text-xs ${sort === s ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`group-sites-sort-${s}`}
                        >
                            {t(`sites.sort_${s}`)}
                        </button>
                    ))}
                </div>
            </div>
            {sites.isLoading ? (
                <div className="space-y-3">
                    <Skeleton className="h-28 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            ) : !sites.data || !kpi ? (
                <EmptyState title={t('sites.unavailable_title')} description={t('sites.unavailable_body')} data-testid="group-sites-unavailable" />
            ) : (
                <>
                    <PooledCard kpi={kpi} cell={sites.data.pooled} scope={sites.data.scope} computedAt={sites.data.freshness.computed_at} />
                    {rows.length === 0 ? (
                        <EmptyState title={t('sites.empty_title')} description={t('sites.empty_body')} data-testid="group-sites-empty" />
                    ) : (
                        <SitesTable org={org} kpi={kpi} rows={rows} />
                    )}
                </>
            )}
        </div>
    )
}

/**
 * The perimeters the caller can read, from the same matrix queries as the
 * access page: entities, agencies and — when the gate lets them through —
 * portfolios. Anonymous rows are left out: a scope you cannot name is not a
 * filter you can pick.
 */
function PerimeterSelect({
    org,
    period,
    value,
    scope,
    onChange,
}: {
    org: string
    period: Period
    value: string | undefined
    scope: GroupPerimeterRef | undefined
    onChange: (uid: string | undefined) => void
}) {
    const { t } = useTranslation('group-steering')
    const entities = useGroupMatrix(org, period, 'entity')
    const agencies = useGroupMatrix(org, period, 'agency')
    const portfolios = useGroupMatrix(org, period, 'portfolio')
    const groups = useMemo(() => {
        const named = (rows: { perimeter: GroupPerimeterRef }[] | undefined) =>
            (rows ?? [])
                .map((r) => r.perimeter)
                .filter((p): p is GroupPerimeterRef & { uid: string } => !p.anonymous && !!p.uid)
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        const byLevel: Record<Level, (GroupPerimeterRef & { uid: string })[]> = {
            entity: named(entities.data?.rows),
            agency: named(agencies.data?.rows),
            portfolio: named(portfolios.data?.rows),
        }
        return LEVELS.map((level) => ({ level, items: byLevel[level] })).filter((g) => g.items.length > 0)
    }, [entities.data, agencies.data, portfolios.data])
    // A perimeter opened from a link but missing from the lists (a stale
    // matrix, a portfolio read through its sheet) still shows its name.
    const known = !value || groups.some((g) => g.items.some((p) => p.uid === value))
    return (
        <Select value={value ?? WHOLE_SCOPE} onValueChange={(v) => onChange(v === WHOLE_SCOPE ? undefined : v)}>
            <SelectTrigger className="h-8 w-full sm:w-64 text-sm" aria-label={t('sites.perimeter_label')} data-testid="group-sites-perimeter">
                <SelectValue placeholder={t('sites.perimeter_all')} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value={WHOLE_SCOPE} data-testid="group-sites-perimeter-all">
                    {t('sites.perimeter_all')}
                </SelectItem>
                {!known && value && (
                    <SelectItem value={value} data-testid={`group-sites-perimeter-${value}`}>
                        {scope?.uid === value && scope.name ? scope.name : t('sites.perimeter_selected')}
                    </SelectItem>
                )}
                {groups.map((g) => (
                    <SelectGroup key={g.level}>
                        <SelectLabel>{t(`level.${g.level}`)}</SelectLabel>
                        {g.items.map((p) => (
                            <SelectItem key={p.uid} value={p.uid} data-testid={`group-sites-perimeter-${p.uid}`}>
                                {p.name || p.code}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                ))}
            </SelectContent>
        </Select>
    )
}

/** The NPS of a cell in its unit, or "—" when withheld. */
function npsValue(cell: GroupSiteCell, kpi: GroupKPIMeta, lang: 'en' | 'fr', locale: string): string {
    return cell.suppressed || cell.value === null ? '—' : formatUnit(cell.value, kpi.unit, lang, locale)
}

function PooledCard({ kpi, cell, scope, computedAt }: { kpi: GroupKPIMeta; cell: GroupSiteCell; scope: GroupPerimeterRef; computedAt?: string }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { formatDate } = useFormatters()
    const counts = npsCounts(cell.breakdown)
    const uncertainty = npsUncertainty(cell.n)
    const withheld = cell.suppressed || cell.value === null
    const scopeName = scope.kind === 'root' || !scope.name ? t('sites.scope_all') : scope.name
    return (
        <Card data-testid="group-sites-pooled">
            <CardContent className="p-4">
                <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <span className="truncate" data-testid="group-sites-scope">{scopeName}</span>
                            <DefinitionPopover kpi={kpi} />
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-3xl font-semibold tabular-nums ${TIER_TEXT[tierOf(cell)]}`} data-testid="group-sites-pooled-value">
                                {npsValue(cell, kpi, lang, locale)}
                            </span>
                            {!withheld && uncertainty !== null && (
                                <span className="text-sm text-muted-foreground tabular-nums" data-testid="group-sites-pooled-uncertainty">
                                    {t('consolidated.uncertainty', { value: uncertainty })}
                                </span>
                            )}
                            {withheld && (
                                <span className="text-xs text-muted-foreground" title={t('sites.withheld_hint', { min: kpi.min_n })} data-testid="group-sites-pooled-withheld">
                                    {t('sites.withheld', { min: kpi.min_n })}
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="text-3xl font-semibold tabular-nums text-foreground" data-testid="group-sites-pooled-n">{cell.n}</div>
                        <div className="text-xs text-muted-foreground" title={t('sites.eligible_hint')}>{t('sites.answers_of', { count: cell.n, closed: cell.eligible_count })}</div>
                    </div>
                    <div className="min-w-[14rem] flex-1 space-y-1.5 self-center">
                        <NpsCompositionBar counts={counts} className="h-3" testId="group-sites-pooled-bar" />
                        <CompositionLegend counts={counts} />
                    </div>
                </div>
                {computedAt && (
                    <p className="mt-3 text-[11px] text-muted-foreground">{t('space.freshness', { date: formatDate(computedAt, { dateStyle: 'short', timeStyle: 'short' }) })}</p>
                )}
            </CardContent>
        </Card>
    )
}

/** Detractors, passives, promoters with their counts and shares, under the pooled bar, in the bar's order. */
function CompositionLegend({ counts }: { counts: NpsCounts }) {
    const { t } = useTranslation('group-steering')
    const share = (n: number) => (counts.total > 0 ? Math.round((n / counts.total) * 100) : 0)
    const items = [
        { key: 'detractors', count: counts.detractors, tone: SURVEY_TONES.detractor },
        { key: 'passives', count: counts.passives, tone: SURVEY_TONES.passive },
        { key: 'promoters', count: counts.promoters, tone: SURVEY_TONES.promoter },
    ]
    return (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="group-sites-legend">
            {items.map((i) => (
                <li key={i.key} className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${i.tone}`} aria-hidden />
                    <span>{t(`sites.${i.key}`)}</span>
                    <span className="tabular-nums text-foreground">{i.count}</span>
                    {counts.total > 0 && <span className="tabular-nums">({share(i.count)} %)</span>}
                </li>
            ))}
        </ul>
    )
}

function SitesTable({ org, kpi, rows }: { org: string; kpi: GroupKPIMeta; rows: GroupSiteRow[] }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    return (
        <Card>
            <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="group-sites-table">
                    <thead className="text-xs text-muted-foreground">
                        <tr className="text-left border-b">
                            <th className="py-2 px-3 md:px-4 font-medium">{t('sites.col_site')}</th>
                            <th className="hidden md:table-cell py-2 pr-4 font-medium">{t('sites.col_agency')}</th>
                            <th className="py-2 pr-4 font-medium w-[30%] min-w-[7rem]">{t('sites.col_composition')}</th>
                            <th className="py-2 pr-3 md:pr-4 font-medium text-right md:whitespace-nowrap" title={t('sites.eligible_hint')}>{t('sites.col_answers')}</th>
                            <th className="py-2 pr-4 font-medium text-right">{t('sites.col_nps')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const counts = npsCounts(r.breakdown)
                            const withheld = r.suppressed || r.value === null
                            return (
                                <tr key={r.asset_uid} className="border-b last:border-b-0 hover:bg-muted/40" data-testid="group-site-row" data-asset-uid={r.asset_uid}>
                                    <td className="py-2 px-3 md:px-4">
                                        <Link to="/$org/assets/$assetUid" params={{ org, assetUid: r.asset_uid }} className="text-foreground hover:underline" data-testid="group-site-link">
                                            {r.name}
                                        </Link>
                                        {r.agency?.name && <div className="md:hidden text-[11px] text-muted-foreground">{r.agency.name}</div>}
                                    </td>
                                    <td className="hidden md:table-cell py-2 pr-4 text-muted-foreground" data-testid="group-site-agency">
                                        {r.agency?.name || '—'}
                                    </td>
                                    <td className="py-2 pr-4">
                                        <div className="space-y-1">
                                            <NpsCompositionBar counts={counts} testId="group-site-bar" />
                                            <div className="flex gap-3 text-[11px] tabular-nums text-muted-foreground" data-testid="group-site-counts">
                                                <CountDot tone={SURVEY_TONES.detractor} count={counts.detractors} label={t('sites.detractors')} />
                                                <CountDot tone={SURVEY_TONES.passive} count={counts.passives} label={t('sites.passives')} />
                                                <CountDot tone={SURVEY_TONES.promoter} count={counts.promoters} label={t('sites.promoters')} />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap" title={t('sites.answers_of', { count: r.n, closed: r.eligible_count })} data-testid="group-site-answers">
                                        <span className="text-foreground">{r.n}</span>
                                        <span className="text-muted-foreground"> / {r.eligible_count}</span>
                                    </td>
                                    <td className="py-2 pr-4 text-right tabular-nums whitespace-nowrap" data-testid="group-site-nps">
                                        {withheld ? (
                                            <span className="text-xs text-muted-foreground" title={t('sites.withheld_hint', { min: kpi.min_n })}>
                                                {t('sites.withheld', { min: kpi.min_n })}
                                            </span>
                                        ) : (
                                            <span className={`font-medium ${TIER_TEXT[tierOf(r)]}`}>{npsValue(r, kpi, lang, locale)}</span>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    )
}

function CountDot({ tone, count, label }: { tone: string; count: number; label: string }) {
    return (
        <span className="inline-flex items-center gap-1" title={`${label}: ${count}`} aria-label={`${label}: ${count}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${tone}`} aria-hidden />
            {count}
        </span>
    )
}
